import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowClockwise,
  Package,
  Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { gmao } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";


const UNIT_LABELS = {
  piece: "pièce",
  meter: "m",
  liter: "L",
  kilogram: "kg",
  other: "unité",
};


function formatMoney(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "Non renseigné";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
    }
  ).format(value);
}


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  return Number.isNaN(
    date.getTime()
  )
    ? value
    : date.toLocaleString(
        "fr-FR",
        {
          dateStyle: "medium",
          timeStyle: "short",
        }
      );
}


export default function InterventionPartUsagePanel({
  interventionId,
  editable = false,
  onChanged,
}) {
  const { user } = useAuth();

  const canSeeCosts = [
    "admin",
    "manager",
  ].includes(user?.role);

  const [parts, setParts] =
    useState([]);

  const [usages, setUsages] =
    useState([]);

  const [summary, setSummary] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [
    selectedPartId,
    setSelectedPartId,
  ] = useState("");

  const [quantity, setQuantity] =
    useState("1");

  const [note, setNote] =
    useState("");


  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [
        usageData,
        partData,
      ] = await Promise.all([
        gmao.listInterventionPartUsages(
          interventionId,
          {
            limit: 1000,
          }
        ),
        editable
          ? gmao.listParts({
              limit: 1000,
            })
          : Promise.resolve([]),
      ]);

      setUsages(
        Array.isArray(
          usageData?.items
        )
          ? usageData.items
          : []
      );

      setSummary(
        usageData || {}
      );

      setParts(
        Array.isArray(partData)
          ? partData.filter(
              (part) =>
                !part.isArchived
            )
          : []
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    } finally {
      setLoading(false);
    }
  }, [
    editable,
    interventionId,
  ]);


  useEffect(() => {
    load();
  }, [load]);


  const selectedPart = useMemo(
    () =>
      parts.find(
        (part) =>
          part.id ===
          selectedPartId
      ) || null,
    [
      parts,
      selectedPartId,
    ]
  );


  const refreshParent = async () => {
    await load();

    if (onChanged) {
      await onChanged();
    }
  };


  const consume = async () => {
    if (!selectedPartId) {
      toast.error(
        "Sélectionnez une pièce."
      );

      return;
    }

    const numericQuantity =
      Number(quantity);

    if (
      Number.isNaN(
        numericQuantity
      ) ||
      numericQuantity <= 0
    ) {
      toast.error(
        "La quantité est invalide."
      );

      return;
    }

    setBusy(true);

    try {
      await gmao.consumeInterventionPart(
        interventionId,
        {
          part_id: selectedPartId,
          quantity: numericQuantity,
          note: note.trim(),
        }
      );

      toast.success(
        "Pièce consommée et stock mis à jour"
      );

      setSelectedPartId("");
      setQuantity("1");
      setNote("");

      await refreshParent();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    } finally {
      setBusy(false);
    }
  };


  const returnUsage = async (
    usage
  ) => {
    if (
      !window.confirm(
        `Restituer ${usage.quantity} ${
          usage.unitSnapshot || ""
        } de ${usage.partNameSnapshot} au stock ?`
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await gmao.returnInterventionPartUsage(
        interventionId,
        usage.id,
        {
          note:
            "Restitution depuis l’ordre de travail",
        }
      );

      toast.success(
        "Pièce restituée au stock"
      );

      await refreshParent();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    } finally {
      setBusy(false);
    }
  };


  return (
    <section
      data-testid="intervention-part-usage-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="label-eyebrow">
            Stock lié
          </div>

          <h3 className="mt-1 font-heading text-lg font-black">
            Pièces consommées
          </h3>

          <p className="mt-1 text-sm text-[var(--do-muted)]">
            Chaque consommation met immédiatement à jour le stock et conserve les informations de la pièce au moment de l’intervention.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          disabled={
            loading ||
            busy
          }
          data-testid="refresh-intervention-parts"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold disabled:opacity-50"
        >
          <ArrowClockwise
            size={16}
            className={
              loading
                ? "animate-spin"
                : ""
            }
          />

          Actualiser
        </button>
      </div>

      {canSeeCosts && (
        <div
          className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
          data-testid="intervention-parts-total-cost"
        >
          <div className="label-eyebrow">
            Coût actif des pièces HT
          </div>

          <div className="mt-1 font-heading text-xl font-black">
            {formatMoney(
              summary.totalCostHt ??
              0
            )}
          </div>
        </div>
      )}

      {editable && (
        <div className="mt-4 rounded-xl border border-[var(--do-border)] p-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_140px]">
            <select
              value={selectedPartId}
              onChange={(event) =>
                setSelectedPartId(
                  event.target.value
                )
              }
              disabled={busy}
              data-testid="intervention-part-select"
              className="rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm"
            >
              <option value="">
                Sélectionner une pièce
              </option>

              {parts.map(
                (part) => (
                  <option
                    key={part.id}
                    value={part.id}
                    disabled={
                      Number(
                        part.quantityOnHand
                      ) <= 0
                    }
                  >
                    {part.reference}
                    {" — "}
                    {part.name}
                    {" — stock "}
                    {part.quantityOnHand}
                  </option>
                )
              )}
            </select>

            <input
              type="number"
              min="0.01"
              step="0.01"
              value={quantity}
              onChange={(event) =>
                setQuantity(
                  event.target.value
                )
              }
              disabled={busy}
              data-testid="intervention-part-quantity"
              className="rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm"
              placeholder="Quantité"
            />
          </div>

          {selectedPart && (
            <div className="mt-2 text-xs text-[var(--do-muted)]">
              Disponible :{" "}
              <strong>
                {selectedPart.quantityOnHand}
                {" "}
                {UNIT_LABELS[
                  selectedPart.unit
                ] ||
                  selectedPart.unit}
              </strong>
              {selectedPart.location
                ? ` · ${selectedPart.location}`
                : ""}
            </div>
          )}

          <textarea
            value={note}
            onChange={(event) =>
              setNote(
                event.target.value
              )
            }
            disabled={busy}
            rows={2}
            maxLength={1000}
            data-testid="intervention-part-note"
            className="mt-3 w-full resize-y rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm"
            placeholder="Note facultative"
          />

          <button
            type="button"
            onClick={consume}
            disabled={
              busy ||
              !selectedPartId
            }
            data-testid="intervention-part-consume"
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
          >
            <Plus
              size={16}
              weight="bold"
            />

            Consommer la pièce
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-4 label-eyebrow lirah-pulse">
          Chargement des consommations
        </div>
      ) : usages.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-[var(--do-border)] p-5 text-sm text-[var(--do-muted)]">
          Aucune pièce du stock n’a encore été consommée.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {usages.map(
            (usage) => {
              const returned =
                Boolean(
                  usage.returnedAt
                );

              return (
                <article
                  key={usage.id}
                  data-testid={
                    `intervention-part-usage-${usage.id}`
                  }
                  className={`rounded-xl border border-[var(--do-border)] p-4 ${
                    returned
                      ? "opacity-60"
                      : ""
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex gap-3">
                      <div className="rounded-lg bg-[var(--do-surface-soft)] p-2">
                        <Package
                          size={18}
                        />
                      </div>

                      <div>
                        <div className="font-heading font-black">
                          {usage.partNameSnapshot}
                        </div>

                        <div className="mt-1 text-xs font-mono-stat text-[var(--do-muted)]">
                          {usage.partReferenceSnapshot}
                        </div>

                        <div className="mt-2 text-sm">
                          Quantité :{" "}
                          <strong>
                            {usage.quantity}
                            {" "}
                            {UNIT_LABELS[
                              usage.unitSnapshot
                            ] ||
                              usage.unitSnapshot}
                          </strong>
                        </div>

                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          {formatDate(
                            usage.createdAt
                          )}
                        </div>

                        {usage.note && (
                          <p className="mt-2 text-sm text-[var(--do-muted)]">
                            {usage.note}
                          </p>
                        )}

                        {returned && (
                          <div className="mt-2 text-xs font-heading font-semibold text-[var(--do-accent)]">
                            Restituée le{" "}
                            {formatDate(
                              usage.returnedAt
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="sm:text-right">
                      {canSeeCosts &&
                        usage.totalCostHt !==
                          null &&
                        usage.totalCostHt !==
                          undefined && (
                          <div className="font-heading font-black">
                            {formatMoney(
                              usage.totalCostHt
                            )}
                          </div>
                        )}

                      {editable &&
                        !returned && (
                          <button
                            type="button"
                            onClick={() =>
                              returnUsage(
                                usage
                              )
                            }
                            disabled={busy}
                            data-testid={
                              `intervention-part-return-${usage.id}`
                            }
                            className="mt-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-xs font-heading font-semibold disabled:opacity-50"
                          >
                            Restituer au stock
                          </button>
                        )}
                    </div>
                  </div>
                </article>
              );
            }
          )}
        </div>
      )}
    </section>
  );
}
